import { forwardRef, useImperativeHandle, useRef } from 'react';

import { type JoiForm } from '@sine/sine-ui-components';

import type { InitialInvitationValues } from '../../Invitations.types';
import { InviteeForm } from './InviteeForm';

interface InviteeListProps {
  inviteeIds: Array<string>;
  initialValues: InitialInvitationValues['invitee'];
  defaultCountryCode?: string;
  onAddEmail: (id: string) => void;
  onDelete: (id: string) => void;
  onRemoveEmail: (id: string) => void;
}

interface InviteeListRef {
  getForms: () => Record<string, JoiForm<InitialInvitationValues['invitee']>>;
}

export const InviteeList = forwardRef<InviteeListRef, InviteeListProps>(
  ({ inviteeIds, initialValues, defaultCountryCode, onAddEmail, onRemoveEmail, onDelete }, ref) => {
    const formsRef = useRef<Record<string, JoiForm<InitialInvitationValues['invitee']>>>({});

    useImperativeHandle(ref, () => ({
      getForms: () => formsRef.current,
    }));

    return (
      <>
        {inviteeIds.map((inviteeId) => (
          <InviteeForm
            key={inviteeId}
            id={inviteeId}
            initialValues={initialValues}
            defaultCountryCode={defaultCountryCode}
            formsRef={formsRef}
            addEmail={() => onAddEmail(inviteeId)}
            removeEmail={() => onRemoveEmail(inviteeId)}
            onDelete={inviteeIds.length > 1 ? () => onDelete(inviteeId) : undefined}
            onFormChange={(form) => {
              formsRef.current[inviteeId] = form;
            }}
          />
        ))}
      </>
    );
  },
);

InviteeList.displayName = 'InviteeList';
